import React, { useState } from 'react';
import { TextField, Typography, Box, Alert } from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import ClickToCopy from '../../ClickToCopy';
import { useNavigate } from 'react-router-dom';
import BaseDialog, {
  DialogTitle,
  DialogContent,
  DialogActions,
} from '../BaseDialog';
import { Button } from '../../buttons';
import { GoogleClassroomImg } from '../../../assets/icons';
import { useClassroomStore } from '../../../hooks/zustand/classrooms';
import { useNewspaperStore } from '../../../hooks/zustand/newspapers';
import { useAuthenticatedUser } from '../../../hooks/user';
import * as ClassroomsAPI from '../../../api/Classrooms';
import * as NewspapersAPI from '../../../api/Newspapers';
import { track, Events } from '../../../utils/analytics';

const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/classroom.courses.readonly'
  + ' https://www.googleapis.com/auth/classroom.profile.emails'
  + ' https://www.googleapis.com/auth/classroom.rosters.readonly';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewTeacherWizard({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const loadClassrooms = useClassroomStore((state) => state.loadClassrooms);
  const addNewspaper = useNewspaperStore((state) => state.addNewspaper);

  const importFromGoogleClassroom = useGoogleLogin({
    flow: 'auth-code',
    scope: GOOGLE_SCOPES,
    ux_mode: 'redirect',
    redirect_uri: `${window.location.origin}/classrooms/providers/oauth/google`,
    select_account: false,
    hint: user?.email || undefined,
  });

  const [step, setStep] = useState(0);
  const [setupMethod, setSetupMethod] = useState<'choose' | 'create'>('choose');
  const [classroomName, setClassroomName] = useState('');
  const [newspaperName, setNewspaperName] = useState('');
  const [newspaperPlaceholder, setNewspaperPlaceholder] = useState('e.g. The Eagle Eye');
  const [editionName, setEditionName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [createdNewspaperId, setCreatedNewspaperId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setStep(0);
    setSetupMethod('choose');
    setClassroomName('');
    setNewspaperName('');
    setEditionName('');
    setJoinCode('');
    setCreatedNewspaperId('');
    onClose();
  };

  const handleCreateClassroom = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await ClassroomsAPI.createClassroom({ name: classroomName });
      const classroom = response.data.data;
      setJoinCode(classroom.join_code || '');
      const schoolName = classroom.school?.name;
      if (schoolName) {
        setNewspaperPlaceholder(`e.g. The ${schoolName} Times`);
      }
      await loadClassrooms();
      setStep(1);
      track(Events.CreateClassroom, { source: 'new_teacher_wizard' });
    } catch {
      setError('Something went wrong creating your classroom. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewspaper = async () => {
    setIsLoading(true);
    setError('');
    try {
      const classrooms = useClassroomStore.getState().classrooms;
      const firstClassroom = classrooms[0];
      const response = await NewspapersAPI.create({
        name: newspaperName,
        scope_type: 'classroom',
        classrooms: firstClassroom ? [{ id: firstClassroom.id }] : [],
      });
      const newspaper = response.data.data;
      addNewspaper(newspaper);
      setCreatedNewspaperId(newspaper.id);
      setStep(2);
      track(Events.CreateNewspaper, { source: 'new_teacher_wizard' });
      // Suggest a seasonal edition name
      const month = new Date().toLocaleString('default', { month: 'long' });
      const year = new Date().getFullYear();
      setEditionName(`${month} ${year}`);
    } catch {
      setError('Something went wrong creating your newspaper. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEdition = async () => {
    setIsLoading(true);
    setError('');
    try {
      await NewspapersAPI.createEdition(createdNewspaperId, {
        name: editionName,
      });
      setStep(3);
      track(Events.CreateEdition, { source: 'new_teacher_wizard' });
    } catch {
      setError('Something went wrong creating your edition. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    handleClose();
    navigate(`/newspaper/${createdNewspaperId}`);
  };

  return (
    <BaseDialog open={isOpen} onClose={handleClose} showBackButton={false}>
      <>
      {step === 0 && setupMethod === 'choose' && (
        <>
          <DialogContent>
            <DialogTitle>Welcome to Pressto!</DialogTitle>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography sx={{ color: '#666', mb: 3, fontSize: '0.95rem' }}>
              How would you like to set up your classroom?
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1rem',
              mt: 1,
            }}>
              {/* 1. Create a new class */}
              <Box
                onClick={() => setSetupMethod('create')}
                sx={{
                  border: '1px solid #C6C6D1',
                  borderRadius: '8px',
                  height: '200px',
                  padding: '24px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  transition: 'all 150ms ease-in-out',
                  background: '#fff',
                  '&:hover': {
                    border: '1px solid #163567',
                    boxShadow: '4px 4px 0px #163567',
                  },
                }}
              >
                <Typography sx={{ fontSize: '2.2rem', mb: 0.5 }}>
                  ✏️
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a', textAlign: 'center', lineHeight: 1.3 }}>
                  Create a new class
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#777', textAlign: 'center' }}>
                  Invite with a join code
                </Typography>
              </Box>

              {/* 2. Google Classroom */}
              <Box
                onClick={() => {
                  track(Events.ImportFromGoogleClassroom, { source: 'new_teacher_wizard' });
                  importFromGoogleClassroom();
                }}
                sx={{
                  border: '1px solid #C6C6D1',
                  borderRadius: '8px',
                  height: '200px',
                  padding: '24px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  transition: 'all 150ms ease-in-out',
                  background: '#fff',
                  '&:hover': {
                    border: '1px solid #163567',
                    boxShadow: '4px 4px 0px #163567',
                  },
                }}
              >
                <img src={GoogleClassroomImg} alt="" height={44} style={{ marginBottom: 4 }} />
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a', textAlign: 'center', lineHeight: 1.3 }}>
                  Import from Google Classroom
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#777', textAlign: 'center' }}>
                  Sync your existing roster
                </Typography>
              </Box>

              {/* 3. Clever (coming soon) */}
              <Box
                sx={{
                  border: '1px solid #E5E5EA',
                  borderRadius: '8px',
                  height: '200px',
                  padding: '24px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  background: '#fafafa',
                  opacity: 0.65,
                  cursor: 'not-allowed',
                }}
              >
                <Box sx={{
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#4270ed',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  marginBottom: '4px',
                }}>
                  C
                </Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a', textAlign: 'center', lineHeight: 1.3 }}>
                  Import from Clever
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#777', textAlign: 'center' }}>
                  Coming soon
                </Typography>
              </Box>

              {/* 4. ClassLink (coming soon) */}
              <Box
                sx={{
                  border: '1px solid #E5E5EA',
                  borderRadius: '8px',
                  height: '200px',
                  padding: '24px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  background: '#fafafa',
                  opacity: 0.65,
                  cursor: 'not-allowed',
                }}
              >
                <Box sx={{
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#00a99d',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '1rem',
                  marginBottom: '4px',
                }}>
                  CL
                </Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a', textAlign: 'center', lineHeight: 1.3 }}>
                  Import from ClassLink
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#777', textAlign: 'center' }}>
                  Coming soon
                </Typography>
              </Box>
            </Box>
          </DialogContent>
        </>
      )}

      {step === 0 && setupMethod === 'create' && (
        <>
          <DialogContent>
            <DialogTitle>Name your classroom</DialogTitle>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography sx={{ color: '#666', mb: 3, fontSize: '0.95rem' }}>
              What do you call this class?
            </Typography>
            <TextField
              label="Classroom name"
              placeholder="e.g. Mrs. Smith's 4th Grade"
              value={classroomName}
              onChange={(e) => setClassroomName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 25 }}
            />
            <Typography
              onClick={() => setSetupMethod('choose')}
              sx={{
                mt: 2,
                fontSize: '0.8rem',
                color: '#888',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { color: '#163567' },
              }}
            >
              ← Back to setup options
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              label={isLoading ? 'Creating...' : 'Next'}
              onClick={handleCreateClassroom}
              disabled={!classroomName.trim() || isLoading}
            />
          </DialogActions>
        </>
      )}

      {step === 1 && (
        <>
          <DialogContent>
            <DialogTitle>Name your newspaper</DialogTitle>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography sx={{ color: '#666', mb: 3, fontSize: '0.95rem' }}>
              Every great newspaper needs a great name.
            </Typography>
            <TextField
              label="Newspaper name"
              placeholder={newspaperPlaceholder}
              value={newspaperName}
              onChange={(e) => setNewspaperName(e.target.value)}
              fullWidth
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button
              label={isLoading ? 'Creating...' : 'Create newspaper'}
              onClick={handleCreateNewspaper}
              disabled={!newspaperName.trim() || isLoading}
            />
          </DialogActions>
        </>
      )}

      {step === 2 && (
        <>
          <DialogContent>
            <DialogTitle>Name your first edition</DialogTitle>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography sx={{ color: '#666', mb: 3, fontSize: '0.95rem' }}>
              {"Each edition is one issue of your newspaper. You'll assign it to students."}
            </Typography>
            <TextField
              label="Edition name"
              placeholder="e.g. Spring 2026"
              value={editionName}
              onChange={(e) => setEditionName(e.target.value)}
              fullWidth
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button
              label={isLoading ? 'Creating...' : 'Create edition'}
              onClick={handleCreateEdition}
              disabled={!editionName.trim() || isLoading}
            />
          </DialogActions>
        </>
      )}

      {step === 3 && (
        <>
          <DialogContent>
            <DialogTitle>{"You're all set!"}</DialogTitle>
            <Typography sx={{ color: '#666', mb: 3, fontSize: '0.95rem' }}>
              {"Your newspaper is ready. When you're ready to invite students, share this code:"}
            </Typography>
            <Box sx={{
              textAlign: 'center',
              py: 3,
              px: 2,
              mb: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '2px dashed #ccc',
            }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 1, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Class join code
              </Typography>
              <ClickToCopy text={joinCode}>
                <Box sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'monospace', color: '#1a1a1a', letterSpacing: '0.15em' }}>
                    {joinCode}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#999', mt: 0.5 }}>
                    Click to copy
                  </Typography>
                </Box>
              </ClickToCopy>
              <Typography sx={{ fontSize: '0.85rem', color: '#666', mt: 1.5 }}>
                Send your students to <strong>pressto.fun</strong> to join!
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              label="Go to my newspaper"
              onClick={handleFinish}
            />
          </DialogActions>
        </>
      )}
      </>
    </BaseDialog>
  );
}
